<script setup>
import { computed, ref, watch } from 'vue';
import HdIcon from './HdIcon.vue';
import { isDirectUrl, isDurableMedia } from '../lib/audio-store.js';

/** One aside the cook made while cooking, pinned in the margin beside the step
 *  it belongs to.
 *
 *  WHAT THIS IS NOW, AND WHY. It used to be a 4px dash of pale ink with a
 *  duration printed under it — a mark, not a control. Two things were wrong
 *  with that. Nobody could tell it was pressable, so the one thing this app
 *  exists for went unheard; and mark + clock stacked to 32px inside a 28px
 *  ruled line, which meant the MARGIN set the height of every step row and no
 *  step could ever occupy a single line.
 *
 *  So: one 24px disc, on one ruled line, that looks like the button it is. The
 *  duration moved into the accessible name, where it costs no height. */
const props = defineProps({
  /** { id, kind: 'voice'|'photo'|'video'|'written', body, mediaUrl, durationSeconds } */
  note: { type: Object, required: true },
  cookName: { type: String, default: '' },
  playing: { type: Boolean, default: false },
  /** 0-1. Drives the sliver across the face of the disc. */
  progress: { type: Number, default: 0 },
});

defineEmits(['play', 'manage']);

/* Press and hold opens the note's own menu — play it, or delete it. A gesture
   rather than a control, because the alternative is a delete affordance parked
   in the margin of every note, and the margin is the one part of this app that
   has to stay looking like paper.
   Undiscoverable on its own, so the sheet says so in words once per recipe.

   500ms, cancelled by any movement: a thumb sliding to scroll a recipe must
   never open a menu. `contextmenu` is the same gesture on a desktop mouse. */
let holdTimer = null;
function cancelHold() {
  if (holdTimer) { clearTimeout(holdTimer); holdTimer = null; }
}
function startHold(emit) {
  cancelHold();
  holdTimer = setTimeout(() => { holdTimer = null; emit(); }, 500);
}

const clock = computed(() => {
  const s = Number(props.note.durationSeconds);
  if (!Number.isFinite(s) || s <= 0) return null;
  return `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}`;
});

/* A photo whose file is gone still has a URL, so v-if on mediaUrl is not
   enough — the browser draws a broken image icon and the margin stops looking
   like a margin. On error we fall back to the same disc every other kind uses,
   which is not a placeholder: the note is still there and still opens.

   isDirectUrl is the second half of that guard: an `idb:` reference and a
   stale `blob:` URL are both strings an image element would try and fail to
   load, so neither is ever put in a src. */
const broken = ref(false);
watch(() => props.note.mediaUrl, () => { broken.value = false; });
const showThumb = computed(
  () => props.note.kind === 'photo' && isDirectUrl(props.note.mediaUrl) && !broken.value
);

/* A note that is meant to have a recording and doesn't. The button stays
   pressable — it shows what was written instead — but it must not imply audio
   that is not there.

   "Doesn't" now covers more than an empty mediaUrl. Notes written before
   recordings were kept durably carry a `blob:` object URL that died with the
   page that made it; it is a non-empty string pointing at nothing, and reading
   it as a recording would put a duration and a play button on silence. */
const silent = computed(
  () => (props.note.kind === 'voice' || props.note.kind === 'video')
    && !isDurableMedia(props.note.mediaUrl)
);

/* THE ONE TEST FOR THE PLAY TRIANGLE: is there something to hear?
   A written note never has one — it is something somebody typed, not something
   she said. Neither does a photo, nor a voice note whose recording is gone.
   Everything that is not audio wears the outlined disc and three ruled lines,
   which reads as "there is something written here", because there is. */
const audible = computed(
  () => (props.note.kind === 'voice' || props.note.kind === 'video') && !silent.value
);

const fill = computed(() => Math.min(1, Math.max(0, Number(props.progress) || 0)));

/* The name says WHAT PRESSING IT DOES, first word, and whose voice it is —
   "Play what Nonna said here". A screen reader running down the margin of a
   recipe is running down a column of identical discs; "voice note" told them
   the kind and not the act. Playing, the act is the opposite one, and the
   shortest true word for it is the whole label. */
const who = computed(() => props.cookName || 'the cook');
const label = computed(() => {
  if (props.playing) return 'Stop';
  if (audible.value) return `Play what ${who.value} said here${clock.value ? `, ${clock.value}` : ''}`;
  if (props.note.kind === 'written') return 'Read the note here';
  if (props.note.kind === 'photo') return `Open the photo ${who.value} left here`;
  return `Read what ${who.value} said here — no recording was saved`;
});
</script>

<template>
  <!-- The slot is exactly one ruled line tall and the disc is 24 of its 28px.
       That containment is the whole fix for steps that used to take two lines
       whatever they said: nothing in this component may be taller than a rule,
       so the WRITING sets the height of a row and the margin follows it. -->
  <span class="hd-row-margin slot">
    <button
      class="tap"
      type="button"
      :aria-label="label"
      @click="$emit('play', note.id)"
      @pointerdown="startHold(() => $emit('manage', note.id))"
      @pointerup="cancelHold"
      @pointerleave="cancelHold"
      @pointercancel="cancelHold"
      @pointermove="cancelHold"
      @contextmenu.prevent="$emit('manage', note.id)"
    >
      <span
        class="hd-disc"
        :class="{ 'hd-disc-written': !audible }"
        :data-playing="playing ? 'true' : 'false'"
      >
        <img
          v-if="showThumb"
          class="thumb"
          :src="note.mediaUrl"
          alt=""
          @error="broken = true"
        />
        <HdIcon v-else-if="audible" :name="playing ? 'pause' : 'play'" :size="9" />
        <HdIcon v-else name="lines" :size="10" :weight="1.9" />

        <!-- Progress, drawn ON the disc rather than around the row.
             A ring outside the disc would be the one thing here allowed to
             exceed 24px, and 24-inside-28 is the constraint the whole redesign
             turns on. So: a sliver across the face, inset from both edges,
             under the triangle and clear of it.

             scaleX, never width. Identical on screen, and this is the only
             thing in the app animating while audio plays, so it must not be
             asking for layout four times a second. A transform is composited;
             `width` reflows the disc and repaints its box. `transform-origin:
             left` is what makes it read as a line being drawn. -->
        <span v-if="audible" class="fill" :style="{ '--fill': fill }" aria-hidden="true"></span>
      </span>
    </button>
  </span>
</template>

<style scoped>
/* No width: in the margin column the grid gives it one, and in the row of
   recipe-level notes above the method it shrink-wraps the disc so a handful
   sit side by side. Height is a rule, always, in both. */
.slot { flex: none; }

/* THE 44px TARGET, CHOSEN AS: an ::after overlay on the button.
 *
 * The button shrink-wraps the 24px disc and gets no padding at all, because
 * padding here would make the button 44px tall and a 44px child cannot sit in
 * a 28px slot — the margin would go back to setting the row height, which is
 * the exact defect being fixed. The pressable area is instead a transparent
 * 44×44 pseudo-element centred on the disc: it hangs 8px past the rule above
 * and below and 10px past each side, hits nothing in layout, and leaves the
 * slot 28px tall. */
.tap {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.tap::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 44px;
  height: 44px;
  transform: translate(-50%, -50%);
}

/* position: relative is this component's, not app.css's — the sliver and the
   thumbnail are positioned against the disc. Scoped, so it lands at (0,2,0)
   and beats the global .hd-disc without needing !important. */
.hd-disc {
  position: relative;
  overflow: hidden;
}

/* Pressed, and while playing. State, not motion: the app has one animated
   moment and it belongs to the sliver. */
.tap:active .hd-disc { transform: scale(0.94); }

.thumb {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: saturate(0.85);
}

.fill {
  position: absolute;
  left: 4px;
  right: 4px;
  bottom: 4px;
  height: 2px;
  border-radius: 1px;
  background: color-mix(in srgb, var(--hd-sheet) 88%, transparent);
  transform: scaleX(var(--fill, 0));
  transform-origin: left center;
  transition: transform 120ms linear;
}

/* Reduced motion removes the tween, not the feedback: the sliver still tracks
   the audio, it just arrives rather than slides. */
@media (prefers-reduced-motion: reduce) {
  .fill { transition: none; }
  .tap:active .hd-disc { transform: none; }
}
</style>
